import React, { useEffect, useState, useMemo, FC } from 'react';
import axios from 'axios';
import { StyledCellText } from 'molecules/Table/styles/Text.styles';
import Table from 'molecules/Table';
import {
  StyledCellContainer,
  StyledTableContainer,
} from 'molecules/Table/styles';
import OrgReportPreview from 'molecules/OrgReportPreview';
import { Button, Stack, Typography } from '@mui/material';
import { IReportParams, ObservabilityReportResponse } from '../types';
import { StyledTablePagination } from 'molecules/Table/styles/Pagination.styles';
import TablePaginationActions from 'molecules/Table/TablePagination/Actions';
import { useBaoSelector } from 'utils/hooks/redux';
// import OverviewSummary from 'organisms/CompanyOverviewBlock/OverviewSummary';

export interface OrgOrdersResponse {
  count: number;
  report: ObservabilityReportResponse[];
}

export const observabilityDescriptionText = `Smart, AI-driven reports combining usage analytics and pricing intelligence for actionable cloud savings.`;

const ObervabilityReport: FC<{ userId?: string }> = ({ userId }) => {
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(0);
  const [reports, setReports] = useState<OrgOrdersResponse>({
    count: 0,
    report: [],
  });
  const [loader, setLoader] = useState(true);
  const [syncing, setSyncing] = useState<boolean>(false);
  const { currentCompany } = useBaoSelector((state) => state.common);

  const [orgReportPreview, setOrgReportPreview] =
    useState<ObservabilityReportResponse | null>(null);

  const formatMonthYear = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });

  const columns = useMemo(
    () => [
      {
        key: 'month',
        label: 'Month',
        sortable: false,
        width: '20%',
        render: ({ report }: ObservabilityReportResponse) => (
          <StyledCellText $textType="bold">
            {formatMonthYear(report.time_now).replace(' ', ', ')}
          </StyledCellText>
        ),
      },
      ...(!userId
        ? [
            {
              key: 'company_name',
              label: 'Company Name',
              sortable: false,
              width: '15%',
              render: ({ company }: ObservabilityReportResponse) => (
                <StyledCellText $textType="default">
                  {company.name}
                </StyledCellText>
              ),
            },
          ]
        : []),
      // Removed according: https://sprints.zoho.com/workspace/zazmic#P71/itemdetails/I2880
      //   {
      //     key: 'project',
      //     label: 'Cloud Connector',
      //     sortable: false,
      //     width: '20%',
      //     render: ({ connector_name }: ObservabilityReportResponse) => (
      //       <StyledCellText $textType="default">{connector_name}</StyledCellText>
      //     ),
      //   },
      {
        key: 'account_name',
        label: 'Account Name',
        sortable: false,
        width: '15%',
        render: ({ report }: ObservabilityReportResponse) => (
          <StyledCellText $textType="default">
            {report.account_name}
          </StyledCellText>
        ),
      },
      {
        key: 'report',
        label: 'Report',
        sortable: false,
        width: '30%',
        render: (report: ObservabilityReportResponse) => (
          <StyledCellContainer $hStack $left $gap="15px">
            <Button
              disabled
              onClick={() => setOrgReportPreview(report)}
              size="small"
              sx={{ maxWidth: '120px' }}
            >
              Pay
            </Button>

            <Button
              onClick={() => setOrgReportPreview(report)}
              variant="text"
              size="small"
              sx={{ maxWidth: '120px' }}
            >
              View Report
            </Button>
          </StyledCellContainer>
        ),
      },
    ],
    [userId]
  );

  //   const summaryData = useMemo(
  //     () => ({
  //       totalAccounts: orgReports.length,
  //       actualCost: orgReports.reduce(
  //         (acc, curr) => acc + curr.Report.annual_saving,
  //         0
  //       ),
  //       potentialCost: orgReports.reduce(
  //         (acc, curr) => acc + curr.Report.annual_spend,
  //         0
  //       ),
  //       savingsPercentage:
  //         orgReports.length === 0
  //           ? 0
  //           : ((orgReports.reduce(
  //               (acc, curr) => acc + curr.Report.annual_spend,
  //               0
  //             ) -
  //               orgReports.reduce(
  //                 (acc, curr) => acc + curr.Report.annual_saving,
  //                 0
  //               )) /
  //               orgReports.reduce(
  //                 (acc, curr) => acc + curr.Report.annual_spend,
  //                 0
  //               )) *
  //             100,
  //     }),
  //     [orgReports]
  //   );

  const handleChangePage = (event: unknown, newPage: number) => {
    setLoader(true);
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (option: number) => {
    setLoader(true);
    setCurrentPage(0);
    setRowsPerPage(option);
  };

  const handleSync = () => {
    setSyncing(true);
    let url = `${process.env.REACT_APP_USER_SERVICE}/reports/finops/sync`;
    if (userId)
      url = `${process.env.REACT_APP_USER_SERVICE}/reports/finops/${currentCompany?.company_hs_id}/sync`;

    axios.post(url).finally(() => {
      setSyncing(false);
    });
  };

  useEffect(() => {
    (() => {
      setLoader(true);
      const params: IReportParams = {
        limit: rowsPerPage,
        page: currentPage + 1,
      };

      let url = `${process.env.REACT_APP_USER_SERVICE}/reports/finops`;
      if (userId) url = `${url}/${currentCompany?.company_hs_id}`;

      axios
        .get(url, { params })
        .then((p) => {
          setReports(p.data);
        })
        .finally(() => {
          setLoader(false);
        });
    })();
  }, [userId, currentPage, rowsPerPage]);

  return (
    <Stack sx={{ m: 2, gap: 2 }}>
      {/* <OverviewSummary
        totalAccounts={summaryData.totalAccounts}
        actualCost={summaryData.actualCost}
        potentialCost={summaryData.potentialCost}
        savingsPercentage={summaryData.savingsPercentage}
      /> */}
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="body2" fontSize="12px" sx={{ px: 2 }}>
          {observabilityDescriptionText}
        </Typography>
        <Stack direction="row" gap={1}>
          <Button
            variant="contained"
            size="small"
            disabled={syncing}
            onClick={handleSync}
            sx={{ width: '120px', p: 0, fontSize: '12px' }}
          >
            {syncing ? 'Syncing...' : 'Sync Reports'}
          </Button>
          <Button
            component="a"
            href="https://eazyops.cloud/sign-in"
            target="_blank"
            variant="contained"
            size="small"
            sx={{ width: '140px' }}
          >
            Order Report
          </Button>
        </Stack>
      </Stack>
      <StyledTableContainer style={{ minWidth: 800 }}>
        <Table
          idKey="id"
          loading={loader}
          columns={columns}
          data={reports?.report || []}
        />
        <StyledTablePagination
          labelDisplayedRows={() => null}
          rowsPerPageOptions={[]}
          count={reports?.count || 0}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          onPageChange={handleChangePage}
          ActionsComponent={(props) => (
            <TablePaginationActions
              {...props}
              labelRowsPerPage="Items per page"
              rowsPerPageOptions={[20, 50, 100, 200]}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        />
      </StyledTableContainer>

      {orgReportPreview && (
        <OrgReportPreview
          report={orgReportPreview}
          onClose={() => {
            document.body.setAttribute('id', '');
            setOrgReportPreview(null);
          }}
        />
      )}
    </Stack>
  );
};

export default ObervabilityReport;
